import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import Modal from '../components/Modal';

const AuthContext = createContext(null);

// A 401 from ZOCS's own login check while signed in means the login is over (the
// token lasts 8 hours): say so once, instead of leaving pages to fail quietly. The
// backend marks those with an X-ZOCS-Session header (auth/dependencies.py); a 401
// without it came from somewhere else (a service ZOCS calls) and must never end
// the session. The login request is left out -- there a 401 is a wrong password,
// which the login form already shows.
//
// Installed when this module loads, not in a useEffect: axios fixes a
// request's interceptors when the request starts, and pages fire their first
// requests before the provider's effects run, so an expired token on opening
// the app would go unnoticed.
let expired = false;
let onExpired = null;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error?.config?.url || '';
    const own = error?.response?.headers?.['x-zocs-session'] === 'invalid'
      // an older backend sends no marker: its own login failures read like this
      || ['Could not validate credentials', 'Not authenticated', 'Account is inactive']
        .includes(error?.response?.data?.detail);
    if (error?.response?.status === 401 && own && !url.includes('/auth/login')
        && localStorage.getItem('zocs_auth')) {
      expired = true;
      if (onExpired) onExpired();
    }
    return Promise.reject(error);
  },
);

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => {
    const saved = localStorage.getItem('zocs_auth');
    return saved ? JSON.parse(saved) : null;
  });
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    onExpired = () => setSessionExpired(true);
    if (expired) setSessionExpired(true);   // a 401 that arrived before this ran
    return () => { onExpired = null; };
  }, []);

  function login(data) {
    expired = false;
    setSessionExpired(false);
    const authData = {
      token: data.access_token,
      staff_id: data.staff_id,
      full_name: data.full_name,
      access_level: data.access_level,
    };
    setAuth(authData);
    localStorage.setItem('zocs_auth', JSON.stringify(authData));
  }

  function logout() {
    expired = false;
    setSessionExpired(false);
    setAuth(null);
    localStorage.removeItem('zocs_auth');
  }

  return (
    <AuthContext.Provider value={{ auth, login, logout }}>
      {children}
      {sessionExpired && (
        <Modal title="You've been logged out" onClose={logout}>
          <p className="text-sm text-gray-600 mb-5">
            Your session has expired. Please log in again to continue.
          </p>
          <button onClick={logout}
            className="w-full bg-zoddle-green text-white font-semibold py-2.5 rounded-xl hover:opacity-90">
            Log in again
          </button>
        </Modal>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
